interface TreasuryLine {
  id: string;
  date: Date;
  amount: number;
  label: string;
  is_fixed_charge?: boolean;
}

interface Impact {
  id: string;
  type: 'SHIFT' | 'SCALE' | 'ADD' | 'SPREAD';
  target_line_id?: string;
  amount?: number;
  percentage?: number;
  delay_days?: number;
  spread_periods?: number;
  label?: string;
}

interface ScenarioParameters {
  months_security: number;
  rupture_mode: 'zero' | 'security';
  initial_treasury: number;
}

interface ScenarioResult {
  lines: TreasuryLine[];
  cumulative: { date: Date; amount: number }[];
  treasury_min: number;
  treasury_min_date: Date;
  security_threshold: number;
  frd: number;
  rupture_date: Date | null;
  runway_days: number | null;
  fixed_charges_monthly: number;
}

function addDays(date: Date, days: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

function addMonths(date: Date, months: number): Date {
  const result = new Date(date);
  result.setMonth(result.getMonth() + months);
  return result;
}

function calculateFixedChargesMonthly(lines: TreasuryLine[]): number {
  const fixedCharges = lines.filter(line => line.is_fixed_charge && line.amount < 0);

  if (fixedCharges.length === 0) return 0;

  const sortedDates = fixedCharges
    .map(line => line.date)
    .sort((a, b) => a.getTime() - b.getTime());

  if (sortedDates.length === 0) return 0;

  const startDate = sortedDates[0];
  const endDate = sortedDates[sortedDates.length - 1];

  const monthsDiff = Math.max(1,
    (endDate.getFullYear() - startDate.getFullYear()) * 12 +
    (endDate.getMonth() - startDate.getMonth()) + 1
  );

  const totalFixedCharges = fixedCharges.reduce((sum, line) => sum + Math.abs(line.amount), 0);

  return totalFixedCharges / monthsDiff;
}

function applyImpacts(baseLines: TreasuryLine[], impacts: Impact[]): TreasuryLine[] {
  let resultLines = [...baseLines];

  for (const impact of impacts) {
    switch (impact.type) {
      case 'SHIFT':
        if (impact.target_line_id && impact.delay_days !== undefined) {
          resultLines = resultLines.map(line => {
            if (line.id === impact.target_line_id) {
              return {
                ...line,
                date: addDays(line.date, impact.delay_days!)
              };
            }
            return line;
          });
        }
        break;

      case 'SCALE':
        if (impact.target_line_id && impact.percentage !== undefined) {
          resultLines = resultLines.map(line => {
            if (line.id === impact.target_line_id) {
              return {
                ...line,
                amount: line.amount * impact.percentage!
              };
            }
            return line;
          });
        }
        break;

      case 'ADD':
        if (impact.amount !== undefined) {
          resultLines.push({
            id: impact.id,
            date: new Date(),
            amount: impact.amount,
            label: impact.label || 'Impact ajouté',
            is_fixed_charge: false
          });
        }
        break;

      case 'SPREAD':
        if (impact.amount !== undefined && impact.spread_periods !== undefined) {
          const amountPerPeriod = impact.amount / impact.spread_periods;
          const startDate = new Date();

          for (let i = 0; i < impact.spread_periods; i++) {
            resultLines.push({
              id: `${impact.id}-${i}`,
              date: addMonths(startDate, i),
              amount: amountPerPeriod,
              label: `${impact.label || 'Impact étalé'} (${i + 1}/${impact.spread_periods})`,
              is_fixed_charge: false
            });
          }
        }
        break;
    }
  }

  return resultLines.sort((a, b) => a.date.getTime() - b.date.getTime());
}

function calculateCumulative(
  lines: TreasuryLine[],
  initialTreasury: number
): { date: Date; amount: number }[] {
  const cumulative: { date: Date; amount: number }[] = [];
  let runningTotal = initialTreasury;

  const sortedLines = [...lines].sort((a, b) => a.date.getTime() - b.date.getTime());

  for (const line of sortedLines) {
    runningTotal += line.amount;
    cumulative.push({
      date: line.date,
      amount: runningTotal
    });
  }

  return cumulative;
}

function findMinimumTreasury(cumulative: { date: Date; amount: number }[]): {
  min: number;
  date: Date;
} {
  if (cumulative.length === 0) {
    return { min: 0, date: new Date() };
  }

  let min = cumulative[0].amount;
  let minDate = cumulative[0].date;

  for (const point of cumulative) {
    if (point.amount < min) {
      min = point.amount;
      minDate = point.date;
    }
  }

  return { min, date: minDate };
}

function findRuptureDate(
  cumulative: { date: Date; amount: number }[],
  threshold: number
): Date | null {
  for (const point of cumulative) {
    if (point.amount < threshold) {
      return point.date;
    }
  }
  return null;
}

function calculateRunwayDays(ruptureDate: Date | null): number | null {
  if (!ruptureDate) return null;

  const today = new Date();
  const diffTime = ruptureDate.getTime() - today.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  return diffDays;
}

export function computeScenario(
  basePlan: TreasuryLine[],
  impacts: Impact[],
  parameters: ScenarioParameters
): ScenarioResult {
  const clonedPlan = basePlan.map(line => ({ ...line }));

  const modifiedLines = applyImpacts(clonedPlan, impacts);

  const cumulative = calculateCumulative(modifiedLines, parameters.initial_treasury);

  const { min: treasury_min, date: treasury_min_date } = findMinimumTreasury(cumulative);

  const fixed_charges_monthly = calculateFixedChargesMonthly(modifiedLines);

  const security_threshold = fixed_charges_monthly * parameters.months_security;

  const frd = treasury_min - security_threshold;

  const threshold = parameters.rupture_mode === 'zero' ? 0 : security_threshold;
  const rupture_date = findRuptureDate(cumulative, threshold);

  const runway_days = calculateRunwayDays(rupture_date);

  return {
    lines: modifiedLines,
    cumulative,
    treasury_min,
    treasury_min_date,
    security_threshold,
    frd,
    rupture_date,
    runway_days,
    fixed_charges_monthly
  };
}

export function compareScenarios(
  baseResult: ScenarioResult,
  scenarioResult: ScenarioResult
): {
  treasury_min_delta: number;
  frd_delta: number;
  runway_delta: number | null;
  rupture_date_delta_days: number | null;
} {
  const treasury_min_delta = scenarioResult.treasury_min - baseResult.treasury_min;
  const frd_delta = scenarioResult.frd - baseResult.frd;

  let runway_delta: number | null = null;
  if (baseResult.runway_days !== null && scenarioResult.runway_days !== null) {
    runway_delta = scenarioResult.runway_days - baseResult.runway_days;
  }

  let rupture_date_delta_days: number | null = null;
  if (baseResult.rupture_date && scenarioResult.rupture_date) {
    const diffTime = scenarioResult.rupture_date.getTime() - baseResult.rupture_date.getTime();
    rupture_date_delta_days = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  }

  return {
    treasury_min_delta,
    frd_delta,
    runway_delta,
    rupture_date_delta_days
  };
}

export type { TreasuryLine, Impact, ScenarioParameters, ScenarioResult };
